import React, { useState, useEffect } from "react";
import { Table, Button, Modal, Form, DatePicker, Input, message, Tag, Space, Select } from "antd";
import axios from "axios";
import DefaultLayout from "../components/DefaultLayout";
import moment from "moment";
import config from "../config";

const { Search } = Input;
const { RangePicker } = DatePicker;
const { Option } = Select;

const InventoryLog = () => {
  const apiBaseUrl = config.REACT_APP_API_BASE_URL;
  const [auditLogs, setAuditLogs] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterModalVisible, setFilterModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });

  // Fetch all audit logs
  const fetchAuditLogs = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${apiBaseUrl}/api/products/get-audit-logs`);
      console.log(response)
      setAuditLogs(response.data.data);
      setFilteredData(response.data.data);
    } catch (error) {
      message.error("Failed to fetch audit logs");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAuditLogs();
  }, []);

  // Search function
  const handleSearch = (searchValue) => {
    const lowerSearchValue = searchValue.toLowerCase();

    if (searchValue) {
      setFilteredData(
        auditLogs.filter(item =>
          item.productName.toLowerCase().includes(lowerSearchValue) ||
          item.user.toLowerCase().includes(lowerSearchValue)
        )
      );
    } else {
      setFilteredData(auditLogs);
    }
  };

  // Apply filters from modal
  const applyFilters = async (values) => {
    try {
      setLoading(true);
      let filtered = [...auditLogs];

      if (values.action) {
        filtered = filtered.filter(item => item.action === values.action);
      }

      if (values.dateRange) {
        const [startDate, endDate] = values.dateRange;
        filtered = filtered.filter(item => 
          moment(item.timestamp).isBetween(startDate, endDate, null, '[]')
        );
      }

      setFilteredData(filtered);
      setFilterModalVisible(false);
    } catch (error) {
      message.error("Failed to apply filters");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Reset filters
  const resetFilters = () => {
    form.resetFields();
    setFilteredData(auditLogs);
    setFilterModalVisible(false);
  };

  // Columns for the audit log table
  const columns = [
    {
      title: "Product Name",
      dataIndex: "productName",
      key: "productName",
      sorter: (a, b) => a.productName.localeCompare(b.productName),
      showSorterTooltip: false,
    },
    {
        title: "Action",
        dataIndex: "action",
        key: "action",
        render: (action) => {
            // Enum mapping - only change the display text here
            const ACTION_DISPLAY = {
            UPDATE_QUANTITY: "QUANTITY UPDATE",
            UPDATE_PO_NUMBER: "PO UPDATE",
            UPDATE_QUANTITY_AND_PO: "QUANTITY & PO UPDATE", 
            CREATE: "CREATE",
            DELETE: "DELETE"
            };

            // Color mapping remains separate
            const ACTION_COLORS = {
            UPDATE_QUANTITY: "blue",
            UPDATE_PO_NUMBER: "cyan",
            UPDATE_QUANTITY_AND_PO: "purple",
            CREATE: "green",
            DELETE: "red"
            };

            return (
            <Tag 
                color={ACTION_COLORS[action] || "geekblue"}
                key={action}
            >
                {ACTION_DISPLAY[action] || action.toUpperCase()}
            </Tag>
            );
        },
        sorter: (a, b) => a.action.localeCompare(b.action),
        showSorterTooltip: false,
        filters: Object.entries({
            UPDATE_QUANTITY: "Quantity Update",
            UPDATE_PO_NUMBER: "PO Update",
            UPDATE_QUANTITY_AND_PO: "Quantity & PO Update",
            CREATE: "Create",
            DELETE: "Delete"
        }).map(([value, text]) => ({ text, value })),
        onFilter: (value, record) => record.action === value
    },
    {
        title: "Quantity Change",
        dataIndex: "quantityChange",
        key: "quantityChange",
        render: (_, record) => {
            const difference = record.newQuantity - record.oldQuantity;
            if (difference === 0) return "NIL";

            return (
            <Space>
                <span style={{ textDecoration: 'line-through' }}>
                {record.oldQuantity === null || record.oldQuantity === undefined
                    ? "NIL"
                    : record.oldQuantity}
                </span>
                <span>→</span>
                <span>
                {record.newQuantity === null || record.newQuantity === undefined
                    ? "NIL"
                    : record.newQuantity}
                </span>
            </Space>
            );
        },
    },
    {
        title: "Quantity Adjustment",
        dataIndex: "quantityAdjustment",
        key: "quantityAdjustment",
        render: (_, record) => {
            const difference = record.newQuantity - record.oldQuantity;

            if (record.newQuantity === undefined || record.oldQuantity === undefined  || difference === 0) return "NIL";

            const isPositive = difference > 0;
            const color = isPositive ? '#52c41a' : '#ff4d4f';
            const sign = isPositive ? '+' : '';

            return (
            <span style={{ color }}>
                {sign}{difference}
            </span>
            );
        },
    },
    {
        title: "PO/Order Number Change",
        dataIndex: "poChange",
        key: "poChange",
        render: (_, record) => {
            // 判断PO Number是否实际发生了变化
            const poChanged = record.oldPoNumber !== record.newPoNumber;
            
            return (
            <Space>
                {poChanged ? (
                <>
                    <span style={{ textDecoration: 'line-through' }}>
                    {record.oldPoNumber || "NIL"}
                    </span>
                    <span>→</span>
                    <span style={{ color: '#1890ff' }}> 
                    {record.newPoNumber || "NIL"}
                    </span>
                </>
                ) : (
                <span>{"NIL"}</span> 
                )}
            </Space>
            );
        },
    },
    // {
    //     title: "Change Details",
    //     dataIndex: "changeDetails",
    //     key: "changeDetails",
    //     render: (text) => (
    //         <div style={{ whiteSpace: 'pre-line' }}>
    //             {text || 'No details'}
    //         </div>
    //     ),
    // },

    {
      title: "User",
      dataIndex: "user",
      key: "user",
      sorter: (a, b) => a.user.localeCompare(b.user),
      showSorterTooltip: false,
    },
    {
      title: "Timestamp",
      dataIndex: "timestamp",
      key: "timestamp",
      render: (timestamp) => moment(timestamp).format("YYYY-MM-DD HH:mm:ss"),
      sorter: (a, b) => moment(a.timestamp).unix() - moment(b.timestamp).unix(),
      showSorterTooltip: false,
      defaultSortOrder: 'descend',
    },
  ];

  return (
    <>
    <DefaultLayout searchPlaceholder="Search by product or user" onSearch={handleSearch}>
      {/* Main content wrapper for blur effect */}
      <div className={`inventorylog-page-wrapper${filterModalVisible ? ' blur-background' : ''}`}>
        <div className="page-header"
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '10px',
          }}
        >
          <h1 className="page-title">Inventory Audit Log</h1>
          <div className="d-flex align-items-center">
            <Button type="primary" onClick={() => setFilterModalVisible(true)}
              style={{ marginRight: 8 }}
            >
              Filter Records
            </Button>
            <Button type="primary" onClick={fetchAuditLogs}
            >
              Refresh
            </Button>
          </div>
        </div>
        <Table
          columns={columns}
          dataSource={filteredData}
          bordered
          loading={loading}
          rowKey="_id"
          // scroll={{ x: true }}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            pageSizeOptions: ['10', '20', '50', '100'],
            showSizeChanger: filteredData.length > 10,
            showTotal: (total, range) => `Showing ${range[0]}-${range[1]} of ${total} items`,
            onChange: (page, pageSize) => setPagination({ current: page, pageSize }),
          }}
        />
      </div>
      {/* Modal is rendered outside the blurred wrapper so it is not blurred */}
      <Modal
        title="Filter Audit Logs"
        visible={filterModalVisible}
        onCancel={() => setFilterModalVisible(false)}
        footer={[
          <Button key="back" onClick={() => setFilterModalVisible(false)}>
            Cancel
          </Button>,
          <Button key="reset" onClick={resetFilters}>
            Reset
          </Button>,
          <Button
            key="submit"
            type="primary"
            onClick={() => form.submit()}
            loading={loading}
          >
            Apply Filters
          </Button>,
        ]}
      >
        <Form form={form} layout="vertical" onFinish={applyFilters}>
          {/* <Form.Item name="action" label="Action Type">
            <Select placeholder="Select action type">
                <Option value="UPDATE_QUANTITY">Quantity Update</Option>
                <Option value="UPDATE_PO_NUMBER">PO Number Update</Option>
                <Option value="UPDATE_QUANTITY_AND_PO">Quantity & PO Update</Option>
                <Option value="CREATE">Create</Option>
                <Option value="DELETE">Delete</Option>
            </Select>
          </Form.Item> */}
          <Form.Item name="dateRange" label="Date Range">
            <RangePicker 
              showTime={{ format: 'HH:mm' }}
              style={{ width: '100%' }}
              ranges={{
                'Today': [moment().startOf('day'), moment().endOf('day')],
                'This Week': [moment().startOf('week'), moment().endOf('week')],
                'This Month': [moment().startOf('month'), moment().endOf('month')],
              }}
            />
          </Form.Item>
        </Form>
      </Modal>
    </DefaultLayout>
    </>
  );
};

export default InventoryLog;